import { useEffect, useState } from "react";
import { usePlant } from "@/lib/context";
import api from "@/lib/api";
import { num, inrFull, COLORS } from "@/lib/format";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell, ReferenceLine } from "recharts";

export default function Air() {
  const { state } = usePlant();
  const [snaps, setSnaps] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/plant/snapshots?hours=24");
        if (alive) setSnaps(r.data.snapshots || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!state) return <div className="p-10 text-[#8F93A2]">Loading…</div>;
  const m = state.metrics || {};
  const tags = state.tags || {};
  const pressure = tags.CA_SYSTEM_PRESSURE || 8;
  const pressureColor = pressure <= 6.5 ? COLORS.green : pressure <= 7.5 ? COLORS.yellow : COLORS.red;

  // CE-21 chart — only emit values when compressor was loaded (Fix 3.3)
  const seData = snaps.map((s) => {
    const loaded = (s.tags?.CA_LOADED ?? 1) > 0.5;
    const flow = s.tags?.CA_FLOW_M3MIN || 0;
    const kw = s.tags?.CA_MAIN_KW || 0;
    const se = (loaded && flow > 0.5) ? kw / flow : null;
    return {
      t: new Date(s.plant_time).toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit" }),
      se,
    };
  });

  const loadBreakdown = [
    { name: "Useful Air", value: Math.max(0, tags.CA_MAIN_KW - m.ce_20_kw - (m.ce_18 || 0) * 5), fill: COLORS.green },
    { name: "Leak", value: (m.ce_18 || 0) * 5, fill: COLORS.red },
    { name: "Overpressure", value: m.ce_20_kw || 0, fill: COLORS.yellow },
  ];

  const runProfile = [
    { name: "Loaded", hours: 14, fill: COLORS.green },
    { name: "Unloaded", hours: 4, fill: COLORS.yellow },
    { name: "Standby", hours: 1, fill: COLORS.textDim },
  ];

  return (
    <div className="space-y-5">
      <div>
        <div className="label-small">Compressed Air Intelligence</div>
        <h1 className="font-display text-3xl uppercase tracking-tight font-bold">Hidden Losses · Real Money</h1>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="ca-pressure-card">
          <div className="label-small">System Pressure vs Required</div>
          <div className="metric-massive mt-2" style={{ color: pressureColor }}>
            {num(pressure, 2)}<span className="text-sm ml-1">bar</span>
          </div>
          <div className="text-xs text-[#8F93A2] mt-1">Required: 6.5 bar (configurable)</div>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-[#8F93A2]">Overpressure penalty</div>
            <div className="metric-mid text-[#FF3333]">{inrFull(m.ce_20)}<span className="text-xs ml-1">/month</span></div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="ca-leak-card">
          <div className="label-small">Estimated Leak Cost</div>
          <div className="metric-large text-[#FF3333] mt-2">{inrFull(m.ce_19)}</div>
          <div className="text-xs text-[#8F93A2]">/month</div>
          <div className="mt-3 text-sm">
            <div>Leak flow: <span className="font-mono">{num(m.ce_18, 2)} m³/min</span></div>
            <div>Compressor kW: <span className="font-mono">{num(tags.CA_MAIN_KW, 1)}</span></div>
            <div>Specific energy: <span className="font-mono">{num(m.ce_21, 2)} kW/(m³/min)</span></div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 panel p-5" data-testid="ca-savings-card">
          <div className="label-small">Total Recoverable</div>
          <div className="metric-large text-[#00FF66] mt-2">{inrFull(m.ce_19 + m.ce_20)}</div>
          <div className="text-xs text-[#8F93A2]">/month</div>
          <div className="mt-4 p-3 bg-[#14151C] border-l-2 border-[#00E5FF] text-xs leading-relaxed">
            Reduce header to 6.5 bar + repair leaks. Payback on ultrasonic leak survey: ~6 weeks.
          </div>
        </div>

        <div className="col-span-12 md:col-span-8 panel">
          <div className="panel-header"><span>Compressor Specific Energy (CE-21) · 24h</span></div>
          <div className="p-3 h-[240px]" data-testid="se-trend">
            <ResponsiveContainer>
              <LineChart data={seData}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <ReferenceLine y={7.0} stroke={COLORS.green} strokeDasharray="3 3" label={{ value: "Baseline 7.0", fill: COLORS.green, fontSize: 10 }} />
                <ReferenceLine y={8.5} stroke={COLORS.red} strokeDasharray="3 3" label={{ value: "Leak threshold 8.5", fill: COLORS.red, fontSize: 10 }} />
                <Line type="monotone" dataKey="se" stroke={COLORS.amber} dot={false} strokeWidth={2} isAnimationActive={false} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-4 panel">
          <div className="panel-header"><span>Load Decomposition</span></div>
          <div className="p-3 h-[240px]" data-testid="load-breakdown">
            <ResponsiveContainer>
              <BarChart data={loadBreakdown} layout="vertical">
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis type="number" stroke={COLORS.textDim} fontSize={10} />
                <YAxis type="category" dataKey="name" stroke={COLORS.textDim} fontSize={11} width={90} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <Bar dataKey="value" isAnimationActive={false}>{loadBreakdown.map((d, i) => <Cell key={i} fill={d.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Compressor Run Profile · today</span></div>
          <div className="p-3 h-[200px]" data-testid="run-profile">
            <ResponsiveContainer>
              <BarChart data={runProfile}>
                <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke={COLORS.textDim} fontSize={10} />
                <YAxis stroke={COLORS.textDim} fontSize={10} />
                <Tooltip contentStyle={{ background: "#0C0D12", border: "1px solid #1F212A" }} />
                <Bar dataKey="hours" isAnimationActive={false}>{runProfile.map((d, i) => <Cell key={i} fill={d.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 panel">
          <div className="panel-header"><span>Leak Survey Log</span></div>
          <div className="p-4 text-sm space-y-2" data-testid="leak-log">
            <Row k="Last survey" v="14 days ago" />
            <Row k="Leaks found" v="11" />
            <Row k="Leaks repaired" v="6" />
            <Row k="Estimated unfixed flow" v={`${num(m.ce_18, 2)} m³/min`} />
            <Row k="Next survey due" v="in 2 weeks" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-[#14151C]">
      <span className="text-[#8F93A2] text-xs uppercase tracking-wider">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
